import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import FormDataContext from "integrations/contexts/FormDataContext";
import { ApiContext } from "integrations/aws/context/Api";
import { SidebarContext } from "integrations/aws/context/Sidebar";
import ValidatedInput from "integrations/aws/common/ValidatedInput";
import FormWrap from "integrations/aws/common/FormWrap";
import { renderOptions } from "integrations/aws/common/Options";
import { ApiRoutes } from "integrations/aws/common/Routes";
import { AWS_AUTH_TYPES } from "integrations/aws/common/constants";
import useFetch from "integrations/hooks/useFetch";
import formValidation from "integrations/aws/utils/formValidation";
import AWSAuthenticationTypes from "integrations/aws/authentication/AWSAuthenticationTypes";
import AWSCustomEndpoints from "integrations/aws/authentication/AWSCustomEndpoints";
import { toAWSRequest } from "integrations/aws/common/formDataAdapter";
const DisappearingInput = styled.input`
  position: fixed;
  top: -500vh;
  left: -500vw;
`;
const StepAuthorize = ({ onChange, onSubmit, sidebarComponent = null }) => {
  const { formData } = useContext(FormDataContext);
  const { clearSidebar, setSidebar } = useContext(SidebarContext);
  const { availableRegions, setRegions, setStreams } = useContext(ApiContext);
  const [formError, setFormError] = useState(null);
  const [fetchRegionsStatus] = useFetch(ApiRoutes.INTEGRATIONS.AWS.REGIONS, setRegions, "GET");
  const [fetchStreamsStatus, setStreamsFetch] = useFetch(
    null,
    (response) => {
      setStreams(response);
      onSubmit();
    },
    "POST",
    toAWSRequest(formData, { region: formData.awsCloudWatchAwsRegion ? formData.awsCloudWatchAwsRegion.value : "" })
  );
  useEffect(() => {
    setStreamsFetch(null);
    if (fetchRegionsStatus.error) {
      setFormError({ full_message: fetchRegionsStatus.error });
    } else if (fetchStreamsStatus.error) {
      const badKey = /security token/g;
      const badSecret = /signing method/g;
      const noStreams = /No Kinesis streams/g;
      if (fetchStreamsStatus.error.match(badKey)) {
        setFormError({
          full_message: fetchStreamsStatus.error,
          nice_message: 'Invalid AWS Key, check out your AWS account for the 20-character long, alphanumeric string that usually starts with the letters "AK"'
        });
      } else if (fetchStreamsStatus.error.match(badSecret)) {
        setFormError({
          full_message: fetchStreamsStatus.error,
          nice_message: "Invalid AWS Secret, it is usually a 40-character long, base-64 encoded string, but you only get to view it once when you create the Key"
        });
      } else if (fetchStreamsStatus.error.match(noStreams)) {
        setStreams({ streams: [] });
        onSubmit();
      } else {
        setFormError({ full_message: fetchStreamsStatus.error });
      }
    }
    return () => {
      setFormError(null);
    };
  }, [fetchRegionsStatus.error, fetchStreamsStatus.error, onSubmit, setStreams, setStreamsFetch]);
  const handleSubmit = () => {
    setStreamsFetch(ApiRoutes.INTEGRATIONS.AWS.KINESIS.STREAMS);
  };
  useEffect(() => {
    if (sidebarComponent) {
      setSidebar(sidebarComponent);
    }
    return () => {
      clearSidebar();
    };
  }, [clearSidebar, setSidebar, sidebarComponent]);
  const authType = formData.awsAuthenticationType && formData.awsAuthenticationType.value;
  const isFormValid = formValidation.isFormValid(
    [
      "awsCloudWatchName",
      "awsCloudWatchAwsRegion",
      ...authType !== AWS_AUTH_TYPES.automatic ? ["awsAccessKey", "awsSecretKey"] : []
    ],
    formData
  );
  return /* @__PURE__ */ React.createElement(
    FormWrap,
    {
      onSubmit: handleSubmit,
      buttonContent: "Authorize & Choose Stream",
      loading: fetchRegionsStatus.loading || fetchStreamsStatus.loading,
      disabled: isFormValid,
      error: formError,
      title: "Create Input & Authorize AWS",
      description: "This integration allows reading messages directly from a Kinesis stream. CloudWatch messages can optionally be forwarded to Kinesis via CloudWatch subscriptions and then processed."
    },
    /* @__PURE__ */ React.createElement(DisappearingInput, { id: "name", type: "text" }),
    /* @__PURE__ */ React.createElement(DisappearingInput, { id: "password", type: "password" }),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        id: "awsCloudWatchName",
        type: "text",
        fieldData: formData.awsCloudWatchName,
        onChange,
        placeholder: "Input Name",
        label: "Input Name",
        autoComplete: "off",
        required: true
      }
    ),
    /* @__PURE__ */ React.createElement(AWSAuthenticationTypes, { onChange }),
    /* @__PURE__ */ React.createElement(
      ValidatedInput,
      {
        id: "awsCloudWatchAwsRegion",
        type: "select",
        fieldData: formData.awsCloudWatchAwsRegion,
        onChange,
        label: "AWS Region",
        help: "The AWS Region your service is running in.",
        disabled: fetchRegionsStatus.loading,
        required: true
      },
      renderOptions(availableRegions, "Choose AWS Region", fetchRegionsStatus.loading)
    ),
    /* @__PURE__ */ React.createElement(AWSCustomEndpoints, { onChange })
  );
};
export default StepAuthorize;
